/**
 * Custom Mega Dropdown
 *
 * On init, moves each panel to <body> so it lives in the root stacking
 * context — this bypasses z-index and opacity stacking contexts created
 * by header-component's sticky/transition styles.
 *
 * Positioning uses the first .header__row's getBoundingClientRect(),
 * which is always reliable regardless of display:contents on #header-group.
 */

const HIDE_DELAY = 160;
let active = null;
let activeTrigger = null;
let hideTimer = null;

/* ── position ──────────────────────────────────────────────── */

function positionPanel(panel, triggerEl) {
  // .header__row is the concrete visible bar — not affected by display:contents
  const row = document.querySelector('#header-component .header__row')
    || document.querySelector('.header__row');
  if (!row) return;
  const rect = row.getBoundingClientRect();
  const top = Math.max(0, rect.bottom);
  panel.style.top = top + 'px';
  panel.style.maxHeight = (window.innerHeight - top) + 'px';

  // Compact panels open leftward from their nav item (right edges aligned), kept inside the viewport
  if (panel.classList.contains('custom-mega-dropdown--compact') && triggerEl) {
    const triggerRect = triggerEl.getBoundingClientRect();
    const maxLeft = document.documentElement.clientWidth - panel.offsetWidth - 16;
    const left = triggerRect.right - panel.offsetWidth;
    panel.style.left = Math.max(16, Math.min(left, maxLeft)) + 'px';
  }
}

/* ── show / hide ───────────────────────────────────────────── */

function show(panel, triggerEl) {
  clearTimeout(hideTimer);
  if (active && active !== panel) {
    active.classList.remove('custom-mega-dropdown--active');
  }
  // Activate first so a compact panel has a width to clamp against
  panel.classList.add('custom-mega-dropdown--active');
  positionPanel(panel, triggerEl);
  active = panel;
  activeTrigger = triggerEl || null;
}

function scheduleHide() {
  clearTimeout(hideTimer);
  hideTimer = setTimeout(() => {
    if (!active) return;
    active.classList.remove('custom-mega-dropdown--active');
    active = null;
  }, HIDE_DELAY);
}

/* ── path helpers ──────────────────────────────────────────── */

function findInPath(path, className) {
  for (const el of path) {
    if (el.nodeType === 1 && el.classList && el.classList.contains(className)) {
      return el;
    }
  }
  return null;
}

function getPanel(triggerText) {
  const normalized = triggerText.trim().toLowerCase();
  let found = null;
  document.querySelectorAll('.custom-mega-dropdown').forEach(p => {
    if ((p.dataset.trigger || '').trim().toLowerCase() === normalized) found = p;
  });
  return found;
}

/* ── events ────────────────────────────────────────────────── */

// Last pointer type seen, so taps can be told apart from real mouse hovers
let lastPointerType = 'mouse';
const trackPointerType = (e) => { lastPointerType = e.pointerType; };
// pointerover also catches a mouse that moves after a tap, so hover works again on touch laptops
document.addEventListener('pointerover', trackPointerType, { capture: true, passive: true });
document.addEventListener('pointerdown', trackPointerType, { capture: true, passive: true });

// Taps fire an emulated mouseleave that would close the panel, so only a real mouse may close it
function hideOnMouseLeave() {
  if (lastPointerType === 'mouse') scheduleHide();
}

document.addEventListener('mouseover', (e) => {
  // Taps fire an emulated mouseover before the click; the click handler below owns touch
  if (lastPointerType !== 'mouse') return;

  const path = e.composedPath ? e.composedPath() : [e.target];

  if (findInPath(path, 'custom-mega-dropdown')) {
    clearTimeout(hideTimer);
    return;
  }

  const titleEl = findInPath(path, 'menu-list__link-title');
  if (titleEl) {
    const panel = getPanel(titleEl.textContent);
    if (panel) { show(panel, findInPath(path, 'menu-list__link') || titleEl); return; }
    if (active) scheduleHide();
    return;
  }

  if (findInPath(path, 'menu-list__link') && active) {
    scheduleHide();
  }
});

/**
 * Touch (iPad landscape, touch laptops): there is no hover, so the first tap on
 * a trigger opens its panel instead of following the link, a second tap on the
 * same trigger navigates, and a tap anywhere else closes the panel.
 */
document.addEventListener('click', (e) => {
  if (lastPointerType === 'mouse') return;

  const path = e.composedPath ? e.composedPath() : [e.target];
  if (findInPath(path, 'custom-mega-dropdown')) return;

  const linkEl = findInPath(path, 'menu-list__link');
  const titleEl = linkEl?.querySelector('.menu-list__link-title');
  const panel = titleEl ? getPanel(titleEl.textContent) : null;

  if (panel && panel !== active) {
    e.preventDefault();
    show(panel, linkEl);
    return;
  }

  if (!panel && active) {
    clearTimeout(hideTimer);
    active.classList.remove('custom-mega-dropdown--active');
    active = null;
  }
});

window.addEventListener('scroll', () => {
  if (!active) return;
  clearTimeout(hideTimer);
  active.classList.remove('custom-mega-dropdown--active');
  active = null;
}, { passive: true });
window.addEventListener('resize', () => { if (active) positionPanel(active, activeTrigger); }, { passive: true });

const headerEl = document.getElementById('header-component');
if (headerEl) headerEl.addEventListener('mouseleave', hideOnMouseLeave);

/* ── Mobile: inline accordion in drawer ─────────────────────── */

function buildMobilePanel(panel) {
  const wrapper = document.createElement('div');
  wrapper.className = 'custom-mega-dropdown-mobile';

  const right = panel.querySelector('.custom-mega-dropdown__right');
  if (!right) return wrapper;

  const clone = right.cloneNode(true);
  // Remove tabindex="-1" so links are reachable when the accordion is open.
  clone.querySelectorAll('[tabindex="-1"]').forEach(el => el.removeAttribute('tabindex'));
  wrapper.appendChild(clone);

  return wrapper;
}

/**
 * Toggle the inline accordion for a given drawer trigger element.
 * Creates the accordion on first call (or after a section re-render wipes it)
 * by looking for the sibling immediately after the <details> or <a> container.
 */
function handleMobileAccordion(triggerEl, panel) {
  // For <summary> elements the logical insert point is after the parent <details>.
  // For <a> elements (flat menus) insert directly after the anchor.
  const detailsEl = triggerEl.tagName === 'SUMMARY' ? triggerEl.closest('details') : null;
  const insertAfter = detailsEl || triggerEl;

  let accordion = insertAfter.nextElementSibling;
  if (!accordion || !accordion.classList.contains('custom-mega-dropdown-mobile')) {
    accordion = buildMobilePanel(panel);
    insertAfter.after(accordion);
  }

  triggerEl.classList.add('mega-drawer-trigger');
  const isOpen = accordion.classList.contains('custom-mega-dropdown-mobile--open');
  accordion.classList.toggle('custom-mega-dropdown-mobile--open', !isOpen);
  triggerEl.setAttribute('aria-expanded', String(!isOpen));
  triggerEl.classList.toggle('mega-mobile-open', !isOpen);
}

/**
 * Patch HeaderDrawer.prototype.open so that tapping a mega-dropdown
 * trigger in the mobile drawer expands the inline accordion instead
 * of sliding to the sub-panel.
 *
 * Patching the prototype (not a single instance) means the intercept
 * survives section re-renders that replace the header-drawer element.
 */
function patchHeaderDrawerForMega() {
  customElements.whenDefined('header-drawer').then(() => {
    const HeaderDrawerClass = customElements.get('header-drawer');
    if (!HeaderDrawerClass || HeaderDrawerClass._megaPatched) return;
    HeaderDrawerClass._megaPatched = true;

    const originalOpen = HeaderDrawerClass.prototype.open;

    HeaderDrawerClass.prototype.open = function(event) {
      // event.target is the proxied target — the element carrying on:click="header-drawer/open",
      // which is the <summary> element. Query its child text span.
      const textSpan = event?.target?.querySelector?.('.menu-drawer__menu-item-text');
      if (textSpan) {
        const panel = getPanel(textSpan.textContent.trim());
        if (panel) {
          // Prevent the native <details> toggle and the slide-in sub-panel.
          event.preventDefault?.();
          handleMobileAccordion(event.target, panel);
          return;
        }
      }
      return originalOpen.call(this, event);
    };
  });

  // Also handle flat <a> drawer links (no header-drawer/open delegation, just href navigation).
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.menu-drawer')) return;

    const menuItem = e.target.closest('.menu-drawer__menu-item');
    if (!menuItem || menuItem.tagName !== 'A') return; // <summary> items handled by open() patch

    const textSpan = menuItem.querySelector('.menu-drawer__menu-item-text');
    if (!textSpan) return;

    const panel = getPanel(textSpan.textContent.trim());
    if (!panel) return;

    e.preventDefault();
    handleMobileAccordion(menuItem, panel);
  });
}

/* ── init: move panels to <body>, then wire mobile ──────────── */

function init() {
  document.querySelectorAll('.custom-mega-dropdown').forEach(panel => {
    // Relocate to body root — escapes header's stacking context entirely
    document.body.appendChild(panel);
    panel.addEventListener('mouseleave', hideOnMouseLeave);
  });

  patchHeaderDrawerForMega();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
